import type { CampaignRanking, RankingReport } from '@/queries/cpmRanking';
import { rankingCountedViews, rankingReportStatus } from './rankingPresentation';
import { RANKING_PLATFORMS, type RankingPlatform } from './rankingCompetition';

export const RANKING_EXAMPLE_CAMPAIGN_ID = 'tli52e2n5tyjjp47e7encgqi';
export type RankingAuditPreference = { key: string; mode: 'real' | 'example' };
export const rankingDataKey = (ranking: CampaignRanking) => `${ranking.campaign_id}:${[...new Set([...ranking.admin_reports, ...ranking.own_reports].map(report => report.id))].sort().join(',')}:${ranking.rows.length}`;

export function rankingAuditSource(ranking: CampaignRanking | undefined, campaignId: string, preference?: RankingAuditPreference, campaignStatus?: string): 'real' | 'example' | 'unavailable' {
  if (!ranking || ranking.campaign_id !== campaignId) return 'unavailable';
  if (!['draft', 'scheduled'].includes(campaignStatus || '') || campaignId !== RANKING_EXAMPLE_CAMPAIGN_ID || !ranking.can_review) return 'real';
  if (preference?.key === rankingDataKey(ranking)) return preference.mode;
  return ranking.admin_reports.length || ranking.own_reports.length || ranking.rows.length ? 'real' : 'example';
}

const count = (value: string | null | undefined) => value && /^\d+$/.test(value) ? BigInt(value) : 0n;
export type RankingDatePoint = { date: string; exactViews: string; views: number };
export type RankingPlatformPoint = { platform: RankingPlatform; exactViews: string; views: number };
export type RankingTimelinePoint = { date: string; eligibleViews: number; exactEligibleViews: string; belowMinimumViews: number; exactBelowMinimumViews: string; approvedViews: number; exactApprovedViews: string; pendingViews: number; exactPendingViews: string; newReports: number };

export function buildRankingAuditMetrics(ranking: CampaignRanking, campaignStart?: string | null, campaignEnd?: string | null) {
  const participants = new Set(ranking.rows.map(row => row.participant_id));
  const eligible = new Set(ranking.rows.filter(row => row.eligible).map(row => row.participant_id));
  const dates = new Map<string, bigint>();
  const platforms = new Map<RankingPlatform, bigint>();
  const cohorts = new Map<string, { eligible: bigint; belowMinimum: bigint; approved: bigint; pending: bigint; newReports: number }>();
  const topReports: RankingReport[] = [];
  const dateFormatter = new Intl.DateTimeFormat('en-CA', { timeZone: ranking.config.timezone, year: 'numeric', month: '2-digit', day: '2-digit' });
  const localDate = (value: string | number) => {
    const parts = dateFormatter.formatToParts(new Date(value));
    return ['year', 'month', 'day'].map(part => parts.find(item => item.type === part)?.value).join('-');
  };
  const cohort = (date: string) => {
    if (!cohorts.has(date)) cohorts.set(date, { eligible: 0n, belowMinimum: 0n, approved: 0n, pending: 0n, newReports: 0 });
    return cohorts.get(date)!;
  };
  const start = Date.parse(campaignStart || '');
  const end = Date.parse(campaignEnd || '');
  if (Number.isFinite(start) && Number.isFinite(end) && end > start) {
    const firstDay = Date.parse(`${localDate(start)}T00:00:00Z`);
    const lastDay = Date.parse(`${localDate(end - 1)}T00:00:00Z`);
    // Step over date labels in UTC, not 24-hour instants in a DST-observing zone.
    // Observed publication/submission dates are merged below, even outside the period.
    for (let day = firstDay; day <= lastDay; day += 86_400_000) cohort(new Date(day).toISOString().slice(0, 10));
  }
  for (const report of ranking.admin_reports) {
    // Sends count all received reports on their submission date, regardless of review.
    cohort(localDate(report.created_at)).newReports++;
    const publicationDate = report.published_at && Number.isFinite(Date.parse(report.published_at)) ? localDate(report.published_at) : null;
    // Pending counters are diagnostic, including owners with no scored row yet.
    // They never enter the counted aggregates, standings or top clips below.
    if (publicationDate && report.submission_id && rankingReportStatus(report) === 'pending'
      && report.tracked_views != null && /^\d+$/.test(report.tracked_views)) {
      cohort(publicationDate).pending += BigInt(report.tracked_views);
    }
    // Admin reports also contain disqualified owners. Official rows define who scores,
    // including participants who have not yet reached the minimum.
    const countedViews = rankingCountedViews(report);
    if (!participants.has(report.participant_id) || countedViews == null || !publicationDate) continue;
    const views = count(countedViews);
    const date = publicationDate;
    dates.set(date, (dates.get(date) ?? 0n) + views);
    platforms.set(report.platform, (platforms.get(report.platform) ?? 0n) + views);
    const point = cohort(date);
    point.approved += views;
    if (eligible.has(report.participant_id)) point.eligible += views;
    else point.belowMinimum += views;
    topReports.push(report);
  }
  topReports.sort((a, b) => {
    const difference = count(b.submission_id ? rankingCountedViews(b) : b.verified_views) - count(a.submission_id ? rankingCountedViews(a) : a.verified_views);
    return difference === 0n ? a.id.localeCompare(b.id) : difference > 0n ? 1 : -1;
  });
  const totalViews = ranking.rows.reduce((sum, row) => sum + count(row.total_views), 0n);
  const approvedClips = ranking.rows.reduce((sum, row) => sum + row.valid_post_count, 0);
  return {
    totalViews: totalViews.toString(), approvedClips,
    totalReports: ranking.admin_reports.length,
    averageViews: approvedClips ? (totalViews / BigInt(approvedClips)).toString() : '0',
    eligibleParticipants: eligible.size,
    participants: ranking.rows.length,
    pending: (ranking.pending_clip_count ?? 0) + ranking.pending_reports_count,
    topReports,
    timeline: [...cohorts].sort(([a], [b]) => a.localeCompare(b)).map(([date, point]): RankingTimelinePoint => ({
      date, eligibleViews: Number(point.eligible), exactEligibleViews: point.eligible.toString(),
      belowMinimumViews: Number(point.belowMinimum), exactBelowMinimumViews: point.belowMinimum.toString(), newReports: point.newReports,
      approvedViews: Number(point.approved), exactApprovedViews: point.approved.toString(),
      pendingViews: Number(point.pending), exactPendingViews: point.pending.toString(),
    })),
    byPublicationDate: [...dates].sort(([a], [b]) => a.localeCompare(b)).map(([date, views]) => ({ date, views: Number(views), exactViews: views.toString() })),
    byPlatform: RANKING_PLATFORMS.filter(platform => platforms.has(platform)).map(platform => ({ platform, exactViews: platforms.get(platform)!.toString(), views: Number(platforms.get(platform)!) })),
  };
}

export const RANKING_REPORT_PAGE_SIZES = [10, 20, 50, 100];
export type RankingReportFilters = { search: string; platform: string; status: string; page: number; pageSize: number; sort: 'newest' | 'published' | 'views' | 'pending' };
export function filterRankingReports(ranking: CampaignRanking, filters: RankingReportFilters) {
  const names = new Map(ranking.rows.map(row => [row.participant_id, row.display_name]));
  const search = filters.search.trim().toLocaleLowerCase();
  const reports = ranking.admin_reports.filter(report => (filters.platform === 'all' || report.platform === filters.platform)
    && (filters.status === 'all' || rankingReportStatus(report) === filters.status)
    && (!search || [names.get(report.participant_id), report.participant_id, report.profile_url, report.url].some(value => value?.toLocaleLowerCase().includes(search))));
  reports.sort((a, b) => {
    if (filters.sort === 'views') {
      const difference = count(b.submission_id ? rankingCountedViews(b) : b.verified_views) - count(a.submission_id ? rankingCountedViews(a) : a.verified_views);
      if (difference !== 0n) return difference > 0n ? 1 : -1;
    }
    if (filters.sort === 'pending' && (rankingReportStatus(a) === 'pending') !== (rankingReportStatus(b) === 'pending')) return rankingReportStatus(a) === 'pending' ? -1 : 1;
    const key = filters.sort === 'published' ? 'published_at' : 'created_at';
    return Date.parse(b[key] || b.created_at) - Date.parse(a[key] || a.created_at) || a.id.localeCompare(b.id);
  });
  const pageCount = Math.max(1, Math.ceil(reports.length / filters.pageSize));
  const page = Math.min(Math.max(1, filters.page), pageCount);
  return { reports: reports.slice((page - 1) * filters.pageSize, page * filters.pageSize), total: reports.length, pageCount, page };
}

/** Local fixtures only. Never pass this result to Apollo or a write operation. */
export function createExampleRanking(real: CampaignRanking): CampaignRanking {
  const reports: RankingReport[] = Array.from({ length: 18 }, (_, index) => {
    const participant = Math.floor(index / 3);
    const publicationDay = 16 + index % 7;
    const status = index === 17 ? 'rejected' : [13, 14, 16].includes(index) ? 'pending' : 'approved';
    const views = String(Math.max(0, [1250000, 920000, 680000, 420000, 120000, Math.min(12000, Math.floor(real.config.min_total_views / 10))][participant] - (index % 3) * 1000));
    return {
      id: `example-report-${index + 1}`, submission_id: `example-clip-${index + 1}`, clip_approval: status === 'approved' ? 'approved' : status === 'rejected' ? 'disapproved' : 'neutral', ranked_views: status === 'approved' ? views : null, views_source: status === 'approved' ? 'official' : null, metrics_status: status === 'approved' ? 'tracked' : 'unknown', participant_id: `example-participant-${participant + 1}`,
      platform: RANKING_PLATFORMS[index % 4], url: '', profile_url: '', evidence_url: '',
      published_at: `2026-10-${publicationDay}T15:00:00.000Z`, created_at: `2026-10-${publicationDay}T${16 + index % 3}:00:00.000Z`,
      reported_views: views, verified_views: status === 'approved' ? views : null, status,
      profile_verified: status === 'approved', ownership_verified: status === 'approved', reviewed_at: status === 'pending' ? null : '2026-10-22T19:00:00.000Z',
    };
  });
  const rows = Array.from({ length: 6 }, (_, index) => {
    const id = `example-participant-${index + 1}`;
    const approved = reports.filter(report => report.participant_id === id && report.status === 'approved');
    const views = approved.reduce((sum, report) => sum + count(report.verified_views), 0n);
    const eligible = views >= BigInt(real.config.min_total_views) && approved.length > 0;
    return { participant_id: id, display_name: `Exemplo / Example ${String(index + 1).padStart(2, '0')}`, total_views: views.toString(), valid_post_count: approved.length, eligible, place: eligible ? index + 1 : null, prize_cents: eligible ? real.config.prizes[index]?.amount_cents ?? 0 : 0, is_self: false };
  });
  return { ...real, rows, admin_reports: reports, own_reports: [], my_row: null, pending_reports_count: 0, pending_clip_count: reports.filter(report => report.status === 'pending').length, can_finalize: false, unresolved_ties: [], review_fingerprint: '', can_submit: false };
}
